import { Link } from '@mui/material'
import { Navigate, UIMatch, generatePath } from 'react-router-dom'
import { DynamicBreadcrumb } from 'routes/breadcrumbs'
import { routePaths } from 'routes/route-paths'
import { CsRoute } from 'routes/types'
import { prepareRoutes } from 'routes/utils/prepareRoutes'
import { useBenefitPlanRatesRoutes } from './plan-rates/useBenefitPlanRatesRoutes'

export const useOrgBenefitPlanRoutes = (): CsRoute => {
	return {
		path: `${routePaths.organizations.edit.benefitPlans.path}/*`,
		handle: {
			crumb: (data: UIMatch<unknown, unknown>) => {
				return (
					<Link
						href={generatePath(routePaths.organizations.edit.benefitPlans.path, {
							id: data.params.id ?? '',
						})}
					>
						Benefit Plans
					</Link>
				)
			},
		},
		children: prepareRoutes(
			[
				{
					index: true,
					lazy: () =>
						import('pages/benefit-plans-v3').then((module) => ({
							Component: module.default,
						})),
				},
				{
					lazy: () => import('routes/benefit-plan-routes').then((module) => ({ Component: module.default })),
					path: `${routePaths.organizations.edit.benefitPlans.edit.path}/*`,
					children: prepareRoutes(
						[
							{
								path: `*`,
								element: <Navigate to='details' replace />,
							},
							{
								path: routePaths.organizations.edit.benefitPlans.details.path,
								lazy: () =>
									import('pages/edit-benefit-plan-v2').then((module) => ({
										Component: module.default,
									})),
							},
							{
								path: `${routePaths.organizations.edit.benefitPlans.marketingContent.path}/*`,
								handle: {
									crumb: (data: UIMatch<any, any>) => (
										<Link
											href={generatePath(routePaths.organizations.edit.benefitPlans.marketingContent.path, {
												benefitPlanId: data.params.benefitPlanId ?? '',
												id: data.params.id ?? '',
											})}
										>
											Marketing Content
										</Link>
									),
								},
								children: prepareRoutes(
									[
										{
											index: true,
											lazy: () =>
												import('pages/benefit-plan-marketing-contents').then((module) => ({
													Component: module.default,
												})),
										},
										// Benefit Plan Marketing Content
										{
											lazy: () =>
												import('pages/edit-benefit-plan-marketing-content').then((module) => ({
													Component: module.default,
												})),
											path: routePaths.organizations.edit.benefitPlans.marketingContent.edit.path,
											handle: {
												crumb: (data) => <DynamicBreadcrumb portalId={data.params.marketingContentId ?? ''} />,
											},
										},
										{
											lazy: () =>
												import('pages/create-benefit-plan-marketing-content').then((module) => ({
													Component: module.default,
												})),
											path: routePaths.organizations.edit.benefitPlans.marketingContent.create.path,
										},
									],
									{
										stripLeading: `${routePaths.organizations.edit.benefitPlans.marketingContent.path}/`,
									},
								),
							},
							// Question Versions -> Questions
							{
								path: routePaths.organizations.edit.benefitPlans.questionVersions.path,
								lazy: () => import('pages/question-versions').then((module) => ({ Component: module.default })),
								handle: {
									crumb: (data: UIMatch<any, any>) => (
										<Link
											href={generatePath(routePaths.organizations.edit.benefitPlans.questionVersions.path, {
												benefitPlanId: data.params.benefitPlanId ?? '',
												id: data.params.id ?? '',
											})}
										>
											Questions
										</Link>
									),
								},
							},
							{
								lazy: () =>
									import('pages/questions').then((module) => ({
										Component: module.default,
									})),
								path: routePaths.organizations.edit.benefitPlans.questionVersions.create.path,
								handle: {
									crumb: (data: UIMatch<any, any>) => (
										<Link
											href={generatePath(routePaths.organizations.edit.benefitPlans.questionVersions.create.path, {
												benefitPlanId: data.params.benefitPlanId ?? '',
												id: data.params.id ?? '',
											})}
										>
											Create Question Version
										</Link>
									),
								},
							},
							{
								lazy: () =>
									import('pages/questions').then((module) => ({
										Component: module.default,
									})),
								path: routePaths.organizations.edit.benefitPlans.questionVersions.edit.path,
								handle: {
									crumb: (data: UIMatch<any, any>) => (
										<Link
											href={generatePath(routePaths.organizations.edit.benefitPlans.questionVersions.edit.path, {
												benefitPlanId: data.params.benefitPlanId ?? '',
												id: data.params.id ?? '',
												questionVersionId: data.params.questionVersionId ?? '',
											})}
										>
											Edit Question Version
										</Link>
									),
								},
							},
							{
								path: `${routePaths.organizations.edit.benefitPlans.rates.path}/*`,
								handle: {
									crumb: (data: UIMatch<any, any>) => (
										<Link
											href={generatePath(routePaths.organizations.edit.benefitPlans.rates.path, {
												benefitPlanId: data.params.benefitPlanId ?? '',
												id: data.params.id ?? '',
											})}
										>
											Rates
										</Link>
									),
								},
								children: useBenefitPlanRatesRoutes(),
							},
							{
								path: routePaths.organizations.edit.benefitPlans.eligibilityRules.path,
								lazy: () =>
									import('pages/eligibility-rules').then((module) => ({
										Component: module.default,
									})),
							},
						],
						{
							stripLeading: `${routePaths.organizations.edit.benefitPlans.edit.path}/`,
						},
					),
					handle: {
						crumb: (data: UIMatch<unknown, unknown>) => (
							<DynamicBreadcrumb portalId={data.params.benefitPlanId ?? ''} />
						),
					},
				},
				{
					lazy: () => import('pages/create-benefit-plan-v2').then((module) => ({ Component: module.default })),
					path: routePaths.organizations.edit.benefitPlans.create.path,
				},
			],
			{
				stripLeading: `${routePaths.organizations.edit.benefitPlans.path}/`,
			},
		),
	}
}
